import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { AlertService } from '@app/_services';
import { MessageService } from 'primeng/api';
import { Table } from 'primeng/table';
import { OwnerStmtService } from '@app/_components/owner-statements/owner-stmt.service';
import { OwnerStmtExpService } from '@app/_components/owner-stmt-exp/owner-stmt-exp.service';
import * as moment from 'moment';

export interface MonthlyStmtExpenseAPI {
    ownerStmtExpId: number;
    ownerStmtId: number;
    propertyCode: string;
    shortTermRentalId: number;
    stmtStatus: number;
    expenseId: number;
    expenseCategory: number;
    name: string;
    description: string;
    expenseDate: string;
    stmtBilledDate: string;
    amountPaidByKBM: number;
    amountPaidByOwner: number;
    showToStmt: boolean;
    expSubTotal: number;
    expTax: number;
    expTotal: number;
    ownerStmtDetails: any[];
}

@Component({
    selector: 'app-monthly-stmt-expenses',
    templateUrl: './monthly-stmt-expenses.component.html',
    styleUrls: ['./monthly-stmt-expenses.component.scss'],
    providers: [MessageService],
    standalone: false
})
export class MonthlyStmtExpensesComponent implements OnInit {
    @ViewChild('dt') dt: Table;

    form1: UntypedFormGroup;
    expenses: MonthlyStmtExpenseAPI[] = [];
    selectedExpenses: MonthlyStmtExpenseAPI[] = [];
    loading = false;
    saving = false;
    exportLoading = false;

    clonedExpenses: { [key: number]: MonthlyStmtExpenseAPI } = {};

    stmtStatusOptions: any[] = [
        { label: 'All', value: null },
        { label: 'Draft', value: 1 },
        { label: 'NeedAttention', value: 2 },
        { label: 'ReadyForReview1', value: 3 },
        { label: 'ReadyForReview2', value: 4 },
        { label: 'ReadyForReview3', value: 5 },
        { label: 'Approved', value: 6 },
        { label: 'Published', value: 7 }
    ];

    showToStmtOptions: any[] = [
        { label: 'All', value: null },
        { label: 'Yes', value: true },
        { label: 'No', value: false }
    ];

    expenseCategoryOptions: any[] = [
        { label: 'All', value: null },
        { label: 'Maintenance', value: 1 },
        { label: 'Housekeeping', value: 2 },
        { label: 'Onboarding', value: 3 },
        { label: 'Inspection', value: 4 },
        { label: 'Other', value: 5 },
        { label: 'Tax', value: 6 }
    ];

    propertyOptions: any[] = [];

    columns: any[] = [
        { field: 'propertyCode', header: 'Property', width: '120px' },
        { field: 'stmtStatus', header: 'Status', width: '130px' },
        { field: 'stmtBilledDate', header: 'Billed Date', width: '110px' },
        { field: 'expenseCategory', header: 'Category', width: '130px' },
        { field: 'name', header: 'Description', width: '200px' },
        { field: 'amountPaidByKBM', header: 'KBM Paid', width: '110px' },
        { field: 'amountPaidByOwner', header: 'Owner Paid', width: '110px' },
        { field: 'showToStmt', header: 'On Stmt', width: '90px' }
    ];

    constructor(
        private formBuilder: UntypedFormBuilder,
        private ownerStmtService: OwnerStmtService,
        private ownerStmtExpService: OwnerStmtExpService,
        private alertService: AlertService,
        private messageService: MessageService
    ) { }

    ngOnInit(): void {
        this.formBuild();
    }

    formBuild(): void {
        const today = new Date();
        // CLAUDE.md: date-only init — use new Date(y, m, d), never bare new Date()
        const currentMonthYear = new Date(today.getFullYear(), today.getMonth(), 1);

        this.form1 = this.formBuilder.group({
            monthYear: [currentMonthYear]
        });

        this.formSearch();
    }

    formSearch(): void {
        const formValue = this.form1.value;

        if (!formValue.monthYear) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Validation',
                detail: 'Please select a month and year',
                life: 3000
            });
            return;
        }

        const m = moment(formValue.monthYear);
        const month = m.month() + 1;
        const year = m.year();

        this.loading = true;
        this.expenses = [];
        this.selectedExpenses = [];

        this.ownerStmtService.getAllExpensesForMonth(month, year).subscribe(
            (res: any) => {
                const data: MonthlyStmtExpenseAPI[] = res?.data?.expenses || res?.data || [];
                this.expenses = data;
                this.updatePropertyOptions();
                this.messageService.add({
                    severity: 'success',
                    summary: 'Loaded',
                    detail: `${this.expenses.length} expense records`,
                    life: 2000
                });
            },
            (err: any) => {
                console.error('Monthly expenses error:', err);
                this.alertService.error(err);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to load expenses',
                    life: 3000
                });
            },
            () => {
                this.loading = false;
            }
        );
    }

    updatePropertyOptions(): void {
        const codes = [...new Set(this.expenses.map(e => e.propertyCode))].sort();
        this.propertyOptions = codes.map(c => ({ label: c, value: c }));
    }

    // ---- Row edit ----

    onRowEditInit(expense: MonthlyStmtExpenseAPI): void {
        this.clonedExpenses[expense.ownerStmtExpId] = { ...expense };
    }

    onRowEditSave(expense: MonthlyStmtExpenseAPI): void {
        const payload: any = {
            ownerStmtExpId: expense.ownerStmtExpId,
            amountPaidByKBM: expense.amountPaidByKBM,
            amountPaidByOwner: expense.amountPaidByOwner,
            showToStmt: expense.showToStmt
        };

        this.saving = true;
        this.ownerStmtExpService.update(payload).subscribe(
            (res: any) => {
                delete this.clonedExpenses[expense.ownerStmtExpId];
                this.messageService.add({
                    severity: 'success',
                    summary: 'Saved',
                    detail: `Expense ${expense.ownerStmtExpId} updated`,
                    life: 2000
                });
            },
            (err: any) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Save failed',
                    detail: err?.error?.message || 'Could not save expense',
                    life: 4000
                });
                this.onRowEditCancel(expense, null);
            },
            () => {
                this.saving = false;
            }
        );
    }

    onRowEditCancel(expense: MonthlyStmtExpenseAPI, index: number): void {
        const original = this.clonedExpenses[expense.ownerStmtExpId];
        if (original) {
            const idx = this.expenses.findIndex(e => e.ownerStmtExpId === expense.ownerStmtExpId);
            if (idx !== -1) {
                this.expenses[idx] = { ...original };
                this.expenses = [...this.expenses];
            }
            delete this.clonedExpenses[expense.ownerStmtExpId];
        }
    }

    // ---- Status helpers ----

    getStatusLabel(status: number): string {
        const found = this.stmtStatusOptions.find(s => s.value === status);
        return found ? found.label : `Status ${status}`;
    }

    getStatusSeverity(status: number): string {
        switch (status) {
            case 1: return 'secondary';
            case 2: return 'danger';
            case 3:
            case 4:
            case 5: return 'warning';
            case 6: return 'success';
            case 7: return 'info';
            default: return 'secondary';
        }
    }

    // ---- Category label ----

    getCategoryLabel(category: number): string {
        const found = this.expenseCategoryOptions.find(c => c.value === category);
        return found ? found.label : `Cat ${category}`;
    }

    // ---- Open statement ----

    openStatement(ownerStmtId: number): void {
        window.open(`/owner-statement/${ownerStmtId}`, '_blank');
    }

    // ---- Export CSV ----

    exportCsv(): void {
        this.exportLoading = true;
        try {
            const m = moment(this.form1.value.monthYear);
            const fileName = `owner-stmt-expenses-${m.format('YYYY-MM')}.csv`;

            const headers = [
                'Property', 'Status', 'Billed Date', 'Category', 'Description',
                'KBM Paid', 'Owner Paid', 'On Stmt', 'StmtId', 'ExpenseId'
            ];

            const rows = this.expenses.map(e => [
                e.propertyCode,
                this.getStatusLabel(e.stmtStatus),
                e.stmtBilledDate || '',
                this.getCategoryLabel(e.expenseCategory),
                (e.name || '').replace(/,/g, ';'),
                e.amountPaidByKBM ?? 0,
                e.amountPaidByOwner ?? 0,
                e.showToStmt ? 'Yes' : 'No',
                e.ownerStmtId,
                e.expenseId
            ]);

            const csvContent = [headers, ...rows]
                .map(row => row.join(','))
                .join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.click();
            URL.revokeObjectURL(url);
        } finally {
            this.exportLoading = false;
        }
    }

    // ---- Totals ----

    get totalKbm(): number {
        return this.expenses.reduce((sum, e) => sum + (e.amountPaidByKBM || 0), 0);
    }

    get totalOwner(): number {
        return this.expenses.reduce((sum, e) => sum + (e.amountPaidByOwner || 0), 0);
    }

    // ---- Column filters ----

    onPropertyFilterChange(event: any): void {
        const vals = event.value && event.value.length > 0 ? event.value : null;
        this.dt.filter(vals, 'propertyCode', 'in');
    }

    // ---- Bulk show/hide on statement ----

    bulkSetShowToStmt(value: boolean): void {
        if (!this.selectedExpenses || this.selectedExpenses.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: 'No selection',
                detail: 'Please select rows first',
                life: 2000
            });
            return;
        }

        const targets = [...this.selectedExpenses];
        targets.forEach(exp => {
            const idx = this.expenses.findIndex(e => e.ownerStmtExpId === exp.ownerStmtExpId);
            if (idx !== -1) {
                this.expenses[idx] = { ...this.expenses[idx], showToStmt: value };
            }
        });
        this.expenses = [...this.expenses];

        let saved = 0;
        let failed = 0;
        targets.forEach(exp => {
            const payload: any = {
                ownerStmtExpId: exp.ownerStmtExpId,
                amountPaidByKBM: exp.amountPaidByKBM,
                amountPaidByOwner: exp.amountPaidByOwner,
                showToStmt: value
            };
            this.ownerStmtExpService.update(payload).subscribe(
                () => {
                    saved++;
                    if (saved + failed === targets.length) {
                        this.messageService.add({
                            severity: failed === 0 ? 'success' : 'warn',
                            summary: 'Bulk update done',
                            detail: `${saved} updated, ${failed} failed`,
                            life: 3000
                        });
                        this.selectedExpenses = [];
                    }
                },
                () => {
                    failed++;
                    if (saved + failed === targets.length) {
                        this.messageService.add({
                            severity: 'warn',
                            summary: 'Bulk update',
                            detail: `${saved} updated, ${failed} failed`,
                            life: 3000
                        });
                        this.selectedExpenses = [];
                    }
                }
            );
        });
    }
}
